import { need } from "./format";

/**
 * The grab handle on the bottom sheet. Tap toggles, and a drag past a threshold
 * decides by direction — a handle that only responded to taps would look
 * draggable and not be, which is worse than no handle.
 *
 * Collapsing keeps the selection: the panel underneath is still built and still
 * updating, it is only out of the way. That is the whole point — watching the
 * map while a route stays selected.
 *
 * Invisible above the breakpoint, where the panel is a sidebar and there is
 * nothing to park.
 */

/** How far the handle must travel before the direction decides, in CSS px. */
const DRAG_THRESHOLD = 24;

export function bindSheet(): void {
  const handle = need<HTMLButtonElement>("sheet-handle");
  const sheet = handle.closest<HTMLElement>(".sidebar");
  if (!sheet) throw new Error("#sheet-handle gehört in .sidebar");

  const setCollapsed = (collapsed: boolean): void => {
    sheet.dataset["collapsed"] = String(collapsed);
    handle.setAttribute("aria-expanded", String(!collapsed));
    handle.setAttribute("aria-label", collapsed ? "Panel ausklappen" : "Panel einklappen");
  };

  setCollapsed(false);

  let downY: number | null = null;
  let dragged = false;

  handle.addEventListener("pointerdown", (e) => {
    downY = e.clientY;
    dragged = false;
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      // no capture; the listeners below still see the gesture
    }
  });

  handle.addEventListener("pointermove", (e) => {
    if (downY === null) return;
    const dy = e.clientY - downY;
    if (Math.abs(dy) < DRAG_THRESHOLD) return;
    setCollapsed(dy > 0);
    dragged = true;
    downY = null;
  });

  const end = (e: PointerEvent): void => {
    // A tap — no drag resolved it — toggles. Anything else was already decided
    // by direction while the finger was moving.
    if (downY !== null && !dragged) setCollapsed(sheet.dataset["collapsed"] !== "true");
    downY = null;
    try {
      if (handle.hasPointerCapture(e.pointerId)) handle.releasePointerCapture(e.pointerId);
    } catch {
      // never captured
    }
  };
  handle.addEventListener("pointerup", end);
  handle.addEventListener("pointercancel", end);

  // The keyboard gets the same toggle for free: a <button> fires click, and a
  // pointer-driven tap is already handled above, so only real key presses reach
  // this without a preceding pointerdown.
  handle.addEventListener("click", (e) => {
    if (e.detail === 0) setCollapsed(sheet.dataset["collapsed"] !== "true");
  });
}

/** Reopens a parked sheet. Called when the selection changes underneath it. */
export function expandSheet(): void {
  const handle = document.getElementById("sheet-handle");
  const sheet = handle?.closest<HTMLElement>(".sidebar");
  if (!sheet || !handle) return;
  sheet.dataset["collapsed"] = "false";
  handle.setAttribute("aria-expanded", "true");
  handle.setAttribute("aria-label", "Panel einklappen");
}
